/**
 * Client-side SAI estimator (runs entirely in browser, no data sent to server).
 * Post-Simplification-Act formula approximation.
 */
const PARENT_ASSET_RATE = 0.12;

function parentIncomeRate(agi) {
  if (agi < 18000) return 0.22;
  if (agi < 37000) return 0.25;
  if (agi < 60000) return 0.29;
  if (agi < 95000) return 0.34;
  if (agi < 143000) return 0.40;
  return 0.47;
}

export function calculateSAI(inputs) {
  const parentAGI = Number(inputs.parentAGI) || 0;
  const studentAGI = Number(inputs.studentAGI) || 0;
  const parentAssets = Number(inputs.parentAssets) || 0;
  const numberInCollege = Math.max(1, Number(inputs.numberInCollege) || 1);
  const parentIncome = Math.max(0, parentAGI * 0.8 * parentIncomeRate(parentAGI));
  const parentAsset = parentAssets * PARENT_ASSET_RATE;
  const studentIncome = Math.max(0, studentAGI * 0.5);
  return Math.max(0, Math.round((parentIncome + parentAsset + studentIncome) / numberInCollege));
}

export function estimatePell(sai) {
  const MAX = 7395;
  if (sai <= 0) return MAX;
  if (sai > 6500) return 0;
  return Math.max(0, Math.round(MAX - sai * 1.1));
}

export function compareScenarios(baseline) {
  const results = [];
  const parentAssets = Number(baseline.parentAssets) || 0;
  const movable = Math.min(parentAssets * 0.5, 23000);
  if (movable > 1000) {
    const alt = { ...baseline, parentAssets: parentAssets - movable };
    const saiAlt = calculateSAI(alt);
    const saiBase = calculateSAI(baseline);
    results.push({
      id: "retirement",
      label: "Contribute to retirement before filing",
      description: `Moving $${Math.round(movable).toLocaleString()} from savings into a 401(k) or IRA removes it from the SAI calculation (retirement accounts are excluded).`,
      before: saiBase, after: saiAlt,
      pellBefore: estimatePell(saiBase), pellAfter: estimatePell(saiAlt),
      action: `Contribute up to $${Math.round(movable).toLocaleString()} to a retirement account before filing.`
    });
  }
  const numberInCollege = Math.max(1, Number(baseline.numberInCollege) || 1);
  if (numberInCollege === 1) {
    // Show what happens with a sibling added (informational only, the FAFSA Simplification Act means this no longer reduces SAI per the new formula, so label accordingly)
    const alt = { ...baseline, numberInCollege: 2 };
    const saiAlt = calculateSAI(alt);
    const saiBase = calculateSAI(baseline);
    results.push({
      id: "sibling_in_college",
      label: "If a sibling also enrolls in college",
      description: "Under the new FAFSA Simplification Act, having multiple siblings in college no longer automatically reduces your SAI, but some schools still consider it for their own institutional aid.",
      before: saiBase, after: saiAlt,
      pellBefore: estimatePell(saiBase), pellAfter: estimatePell(saiAlt),
      action: "No FAFSA action; ask each school's aid office whether they use a multi-enrollment adjustment.",
      informational: true,
    });
  }
  return results;
}
